/* eslint-disable complexity */
import { useEffect, useState } from 'react';

import { InputWithLabel, PrimaryButton, Show } from '@components/common';

import { AlertError, AlertSuccess } from '@components/widgets';
import { useGetCurrentUser } from '@hooks/index';
import useBillingData from '@shared/hooks/payment/useBillingData';
import { isEmpty } from 'lodash';
import './profileBilling.scss';

const initialErrorState = {
    companyName: true,
    po: false,
    billingEmail: false,
    country: false,
    address1: false,
    city: false,
    postalCode: false,
    taxType: false,
    taxId: false,
};

const ProfileBilling = () => {
    const [error, setError]             = useState(initialErrorState);
    const [alertStatus, setAlertStatus] = useState<{show: boolean; status: 'idle' | 'success' | 'error'; message: string}>({
        show: false,
        status: 'idle',
        message: '',
    });
    const user                          = useGetCurrentUser();
    const {
        isLoading,
        billingData,
        update,
    }                                   = useBillingData({ fetchOnInit: true });
    const [state, setState]             = useState({
        email: billingData?.email || user?.email || '',
        companyName: billingData?.companyName || '',
        po: billingData?.poNumber || '',
        billingEmail: billingData?.billingEmail || '',
        country: billingData?.country || '',
        address1: billingData?.address || '',
        address2: billingData?.address || '',
        city: billingData?.city || '',
        postalCode: billingData?.postalCode || '',
        taxType: billingData?.businessTaxType || '',
        taxId: billingData?.businessTaxId || '',
    });

    useEffect(() => {
        if (!billingData) {
            return;
        }

        setState({
            email: billingData?.email || user?.email || '',
            companyName: billingData?.companyName || '',
            po: billingData?.poNumber || '',
            billingEmail: billingData?.billingEmail || '',
            country: billingData?.country || '',
            address1: billingData?.address || '',
            address2: billingData?.address2 || '',
            city: billingData?.city || '',
            postalCode: billingData?.postalCode || '',
            taxType: billingData?.businessTaxType || '',
            taxId: billingData?.businessTaxId || '',
        });
    }, [billingData]);

    const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (isLoading){
            return;
        }

        setError(initialErrorState);
        setAlertStatus({ show: false, status: 'idle', message: '' });

        if (
            isEmpty(state.companyName) ||
            isEmpty(state.billingEmail) ||
            isEmpty(state.country) ||
            isEmpty(state.address1) ||
            isEmpty(state.city) ||
            isEmpty(state.postalCode) ||
            isEmpty(state.taxType) ||
            isEmpty(state.taxId)
        ) {
            setError({
                ...error,
                companyName: isEmpty(state.companyName),
                billingEmail: isEmpty(state.billingEmail),
                country: isEmpty(state.country),
                address1: isEmpty(state.address1),
                city: isEmpty(state.city),
                postalCode: isEmpty(state.postalCode),
                taxType: isEmpty(state.taxType),
                taxId: isEmpty(state.taxId),
            });

            return;
        }


        update({
            email: state.email || user?.email || '',
            companyName: state.companyName,
            billingEmail: state.billingEmail,
            country: state.country,
            city: state.city,
            address: state.address1,
            address2: state.address2,
            postalCode: state.postalCode,
            poNumber: state.po,
            businessTaxType: state.taxType,
            businessTaxId: state.taxId,
        })
            .then((res) => {
                console.log('res:', res);

                if (res) {
                    setAlertStatus({
                        show: true,
                        status: 'success',
                        message: 'Your billing information has been successfully updated.',
                    });

                    return;
                }

                throw new Error('Failed to update billing information!');
            })
            .catch(() => {
                setAlertStatus({
                    show: true,
                    status: 'error',
                    message: 'Failed to update billing information!',
                });
            });
    };

    const noFieldChanged = billingData?.email ? (
        billingData.email === state.email &&
        billingData.companyName === state.companyName &&
        billingData.billingEmail === state.billingEmail &&
        billingData.country === state.country &&
        billingData.address === state.address1 &&
        billingData.city === state.city &&
        billingData.postalCode === state.postalCode &&
        billingData.poNumber === state.po &&
        billingData.businessTaxId === state.taxId &&
        billingData.businessTaxType === state.taxType
    ) : false;

    return (
        <div className='profile-billing'>
            <div>
                <h2>Billing</h2>
                <p>Changes to these preferences will apply to future invoices only.</p>
            </div>

            <form onSubmit={onSubmit}>
                <InputWithLabel
                    title='Email address'
                    type="email"
                    value={state.email}
                    onChange={(e) => setState({ ...state, email: e.target.value })}
                    disabled
                    placeholder='Enter email address'
                />
                <InputWithLabel
                    showError={false}
                    error={error.companyName ? 'yes' : ''}
                    title='Company name'
                    type="text"
                    value={state.companyName}
                    onChange={(e) => setState({ ...state, companyName: e.target.value })}
                    placeholder='Enter company name'
                />
                <InputWithLabel
                    showError={false}
                    error={error.po ? 'yes' : ''}
                    title='Purchase order (PO) number'
                    type="text"
                    value={state.po}
                    onChange={(e) => setState({ ...state, po: e.target.value })}
                    placeholder='Enter po'
                />
                <InputWithLabel
                    showError={false}
                    error={error.billingEmail ? 'yes' : ''}
                    title='Billing email'
                    type="email"
                    value={state.billingEmail}
                    onChange={(e) => setState({ ...state, billingEmail: e.target.value })}
                    placeholder='You can include multiple items in email billing'
                />

                <div className='pb-form-head'>
                    <h3>Primary business address</h3>
                    <p>This is the physical address of the company purchasing Indepth Analytics services and is used to calculate any applicable sales tax.</p>
                </div>

                <InputWithLabel
                    showError={false}
                    error={error.country ? 'yes' : ''}
                    title='Country'
                    type="text"
                    value={state.country}
                    onChange={(e) => setState({ ...state, country: e.target.value })}
                    placeholder='Enter country'
                />
                <div className='pb-address'>
                    <InputWithLabel
                        showError={false}
                        error={error.address1 ? 'yes' : ''}
                        title='Full address'
                        type="text"
                        value={state.address1}
                        onChange={(e) => setState({ ...state, address1: e.target.value })}
                        placeholder='Enter address 1'
                    />
                    <InputWithLabel
                        noLabel
                        type="text"
                        value={state.address2}
                        onChange={(e) => setState({ ...state, address2: e.target.value })}
                        placeholder='Enter address 2'
                    />
                </div>
                <div className='pb-group'>
                    <InputWithLabel
                        showError={false}
                        error={error.city ? 'yes' : ''}
                        title='City'
                        type="text"
                        value={state.city}
                        onChange={(e) => setState({ ...state, city: e.target.value })}
                        placeholder='Enter city'
                    />
                    <InputWithLabel
                        showError={false}
                        error={error.postalCode ? 'yes' : ''}
                        title='Postal code'
                        type="text"
                        value={state.postalCode}
                        onChange={(e) => setState({ ...state, postalCode: e.target.value })}
                        placeholder='Enter postal code'
                    />
                </div>

                <div className='pb-form-head'>
                    <h3>Business Tax ID</h3>
                </div>
                <div className="pb-group">
                    <InputWithLabel
                        showError={false}
                        error={error.taxType ? 'yes' : ''}
                        title='Type'
                        type="text"
                        value={state.taxType}
                        onChange={(e) => setState({ ...state, taxType: e.target.value })}
                        placeholder='Enter type'
                    />
                    <InputWithLabel
                        showError={false}
                        error={error.taxId ? 'yes' : ''}
                        title='ID'
                        type="text"
                        value={state.taxId}
                        onChange={(e) => setState({ ...state, taxId: e.target.value })}
                        placeholder='Enter your business ID'
                    />
                </div>

                <PrimaryButton disabled={isLoading || noFieldChanged} isLoading={isLoading} type="submit">
                    Save changes
                </PrimaryButton>

                <Show.When isTrue={alertStatus.show}>
                    <Show.When isTrue={alertStatus?.status === 'success'}>
                        <AlertSuccess message={alertStatus?.message}/>
                    </Show.When>
                    <Show.When isTrue={alertStatus?.status === 'error'}>
                        <AlertError message={alertStatus?.message} />
                    </Show.When>
                </Show.When>

            </form>
        </div>
    );
};

export default ProfileBilling;
